import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDatabase, ref, onValue } from "firebase/database";
import { useAccount } from "../context/AccountContext";
import {
  FaTruck,
  FaRoute,
  FaBoxOpen,
  FaBroadcastTower,
} from "react-icons/fa";

import {
  MdLocalShipping,
  MdOutlineInventory2,
} from "react-icons/md";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import "./PartialDispatchMap.css";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PartialDispatchMap = () => {
  const { currentAccount } = useAccount();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [transporterProfiles, setTransporterProfiles] = useState({});
    const navigate = useNavigate();

  useEffect(() => {
    if (!selectedOrder) return;

    const updated = orders.find(o => o.id === selectedOrder.id);
    if (updated) {
      setSelectedOrder(updated); // 🔥 refresh selected order with new data
    }
  }, [orders]);
  useEffect(() => {
    if (orders.length === 0) {
      setSelectedOrder(null);
      return;
    }

    // Don't change if the current selected order still exists
    if (
      selectedOrder &&
      orders.some((order) => order.id === selectedOrder.id)
    ) {
      return;
    }

    // Open the first order (LIVE order because orders are already sorted)
    setSelectedOrder(orders[0]);
  }, [orders]);

  // Fetch Transporter Profiles
  useEffect(() => {
    const db = getDatabase();
    const profilesRef = ref(db, "transporter-profile");

    const unsub = onValue(profilesRef, (snapshot) => {
      if (snapshot.exists()) {
        setTransporterProfiles(snapshot.val());
      }
    });

    return () => unsub();
  }, []);

  // Fetch Orders (Real-time)
  useEffect(() => {
    if (!currentAccount?.uid) return;

    const db = getDatabase();
    const ordersRef = ref(db, "transport_orders");

    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setOrders([]);
        return;
      }

      const list = [];

      Object.entries(data).forEach(([transporterId, transporterOrders]) => {
        Object.entries(transporterOrders || {}).forEach(([id, order]) => {
          const isMyOrder =
            order.clientUid === currentAccount.uid ||
            order.clientId === currentAccount.uid ||
            order.companyName === currentAccount.company_name;

          const isPartial = (order.status || "").toLowerCase() === "partial dispatched";

          if (isMyOrder && isPartial) {
            const profile = transporterProfiles[transporterId] || {};
            list.push({
              id,
              transporterId,
              transporterName: profile.company || "Unknown Transporter",
              transporterPhone: profile.phone || "N/A",
              ...order,
            });
          }
        });
      });


      const sortedList = list.sort((a, b) => {
        const aLive = a?.dispatch?.items?.some(i => i?.location?.sharing === true);
        const bLive = b?.dispatch?.items?.some(i => i?.location?.sharing === true);

        if (aLive && !bLive) return -1;
        if (!aLive && bLive) return 1;

        return (b.orderCreatedAt || b.createdAt || 0) - (a.orderCreatedAt || a.createdAt || 0);
      });

      setOrders(sortedList);
    });

    return () => unsubscribe();
  }, [currentAccount?.uid, currentAccount?.company_name, transporterProfiles]);

  return (
    <div>
 <button
          className="trans-back-button"
          onClick={() => navigate("/dashboard/transport")}
        >
          <ArrowLeft size={20} />
          Back to  Dashboard
        </button>
    <div className="pd-container">
      
      {/* LEFT SIDE - Orders List */}
      <div className="track-pd-left">

        {/* Header */}

        <div className="track-header">

          <div className="header-left">

            <div className="header-icon">
              <MdLocalShipping />
            </div>

            <div>
              <h2 className="text-white">Dispatch Orders</h2>
              <p>Select an order to track</p>
            </div>

          </div>

          <div className="header-stats">

            <div className="stat-card">
              <span>Total</span>
              <strong>{orders.length}</strong>
            </div>

            <div className="stat-card live">

              <span>Live</span>

              <strong>
                {
                  orders.filter(order =>
                    order?.dispatch?.items?.some(
                      item => item?.location?.sharing
                    )
                  ).length
                }
              </strong>

            </div>

          </div>

        </div>

        {orders.length === 0 ? (

          <div className="empty-orders">

            <MdOutlineInventory2 size={55} />

            <p>No partial dispatch orders</p>

          </div>

        ) : (

          orders.map((order) => {

            const isLive = order?.dispatch?.items?.some(
              (i) => i?.location?.sharing === true
            );

            return (

              <div
                key={order.id}
                className={`track-pd-card ${selectedOrder?.id === order.id
                    ? "active"
                    : ""
                  }`}
                onClick={() => setSelectedOrder(order)}
              >

                {/* Top */}

                <div className="track-card-header">

                  <div>

                    <h5>{order.orderId}</h5>

                    <span className="transporter">
                      <b>Tranporter:</b>   {order.transporterName}
                    </span>

                  </div>

                  <span
                    className={`badge ${isLive ? "on" : "off"
                      }`}
                  >

                    {isLive ? (
                      <>
                        <span className="status-dot"></span>
                        LIVE
                      </>
                    ) : (
                      "Offline"
                    )}

                  </span>

                </div>

                {/* Details */}

                <div className="card-details">

                  <div className="detail-row">
                    📍
                    <span>{order.from} → {order.to}</span>
                  </div>

                  <div className="detail-grid">

                    <div className="detail-item">
                      <FaTruck className="detail-icon" />
                      <span>
                        {order.dispatch?.items?.[0]?.truckNumber ||
                          order.truckNumber ||
                          "N/A"}
                      </span>
                    </div>

                    <div className="detail-item">
                      📦Qty
                      <span>
                        {order.quantity} {order.quantityUnit}
                      </span>
                    </div>

                  </div>

                </div>

              </div>



            );

          })

        )}

      </div>

      {/* RIGHT SIDE - Live Tracking */}
      <div className="pd-right">
        {selectedOrder ? (
          <LiveTracking key={selectedOrder.id} order={selectedOrder} />
        ) : (
          <div className="pd-placeholder">
            <div className="placeholder-card">
              <h3>🚛 Live Tracking</h3>
              <p>Select an order from the sidebar to view live tracking.</p>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

function LiveTracking({ order }) {
  const [address, setAddress] = React.useState("");
  const [pulseScale, setPulseScale] = React.useState(1);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  const mapRef = React.useRef(null);
  const timeoutRef = React.useRef(null);
  const growingRef = React.useRef(true);

  const items = order?.dispatch?.items || [];

  const liveItem = React.useMemo(() => {
    return (
      items.find(i => i?.location?.sharing === true) ||
      items.find(i => i?.location) ||
      items[0]
    );
  }, [items]);

  const rawLocation = liveItem?.location;
  const location = rawLocation ? {
    ...rawLocation,
    heading: Number(rawLocation.heading ?? 0),
  } : null;

  const isLive = !!location?.sharing;
  const heading = Number(location?.heading ?? 0);

  const isMapReady = isLoaded && typeof window !== "undefined" && !!window.google;

  // Smooth Pulse Animation - Only when LIVE
  React.useEffect(() => {
    if (!isLive) {
      setPulseScale(1);
      return;
    }

    const interval = setInterval(() => {
      setPulseScale((prev) => {
        if (prev >= 1.45) growingRef.current = false;
        if (prev <= 1) growingRef.current = true;
        return growingRef.current ? prev + 0.04 : prev - 0.04;
      });
    }, 110);

    return () => clearInterval(interval);
  }, [isLive]);

  // Pan to location
  React.useEffect(() => {
    if (!isMapReady || !mapRef.current || !location?.lat || !location?.lng) return;
    mapRef.current.panTo({ lat: location.lat, lng: location.lng });
    mapRef.current.setZoom(16);
  }, [location?.lat, location?.lng, isMapReady]);

  // Geocoding
  React.useEffect(() => {
    if (!isMapReady || !location?.lat || !location?.lng) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: location.lat, lng: location.lng } },
        (results, status) => {
          if (status === "OK" && results?.[0]) {
            setAddress(results[0].formatted_address);
          }
        }
      );
    }, 1100);
  }, [location?.lat, location?.lng, isMapReady]);

  // Arrow Icon (Changes color based on live status)
  const directionIcon = React.useMemo(() => {
    if (!window.google) return null;

    const color = isLive ? "#1E88E5" : "#9E9E9E"; // Blue when live, Grey when off

    return {
      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 7.5,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: "#FFFFFF",
      strokeWeight: 2.5,
      rotation: heading,
      anchor: new window.google.maps.Point(0, 2),
    };
  }, [heading, isLive]);

  if (!isMapReady || !location?.lat || !location?.lng) {
    return <p>Loading map...</p>;
  }

  return (
    <div className="tracking-container">
      <div className="tracking-header">
        <h3>Live Tracking - {order.orderId}</h3>
        <span className={`status-badge ${isLive ? "on" : "off"}`}>
          {isLive ? (
            <>
              <span className="status-dot"></span>
              LIVE
            </>
          ) : (
            "LOCATION OFF"
          )}
        </span>
      </div>

      <div className="live-address text-primary mb-4">
        📍 {address || "Fetching address..."}
      </div>

      <div className="info-grid">
        <div className="info-card">
          <span className="label">Transporter</span>
          <span className="value">{order.transporterName}</span>
        </div>
        <div className="info-card">
          <span className="label">Transporter Phone</span>
          <span className="value">{order.transporterPhone}</span>
        </div>
        <div className="info-card">
          <span className="label">Driver Name</span>
          <span className="value">{liveItem?.driverName || "N/A"}</span>
        </div>
        <div className="info-card">
          <span className="label">Driver Phone</span>
          <span className="value">{liveItem?.driverNumber || "N/A"}</span>
        </div>
        <div className="info-card">
          <span className="label">Truck Number</span>
          <span className="value">{liveItem?.truckNumber || "N/A"}</span>
        </div>
        <div className="info-card">
          <span className="label">🚀 Speed</span>
          <span className="value">
            {location?.speed ? location.speed.toFixed(1) : 0} km/h
          </span>
        </div>
      </div>

      <div className="map-wrapper">
        <GoogleMap
          onLoad={(map) => (mapRef.current = map)}
          zoom={16}
          center={{ lat: location.lat, lng: location.lng }}
          mapContainerStyle={{ width: "100%", height: "460px" }}
        >
          {window.google && directionIcon && (
            <>
              {/* Pulsing Glow - Only when LIVE */}
              {isLive && (
                <>
                  {/* Outer glow */}
                  <Marker
                    position={{ lat: location.lat, lng: location.lng }}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 18 * pulseScale,
                      fillColor: "#1E88E5",
                      fillOpacity: 0.12,
                      strokeColor: "#1E88E5",
                      strokeWeight: 1,
                      strokeOpacity: 0.2,
                    }}
                  />
                  {/* Inner glow */}
                  <Marker
                    position={{ lat: location.lat, lng: location.lng }}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 13 * pulseScale * 0.85,
                      fillColor: "#1E88E5",
                      fillOpacity: 0.25,
                      strokeColor: "#1E88E5",
                      strokeWeight: 1.5,
                      strokeOpacity: 0.35,
                    }}
                  />
                </>
              )}

              {/* Main Arrow - Always shown, grey when location off */}
              <Marker
                key={`${location.lat}-${location.lng}-${heading}-${isLive}`}
                position={{ lat: location.lat, lng: location.lng }}
                icon={directionIcon}
              />
            </>
          )}
        </GoogleMap>
      </div>
    </div>
  );
}

export default PartialDispatchMap;